import React, { useEffect, useMemo, useState } from 'react';
import { PapeletaCurrency } from '../../AdminClasses';
import { Link, useParams } from 'react-router-dom';
import { NotFound } from '../../components';
import { Button, Dropdown, Header, Icon, Stat, Table, Field, Modal } from 'react-frontier2';
import { addCommas, bindClick, toCents } from '@arema/components/Util';
import { useTranslation } from 'react-i18next';
import { firstBy } from 'thenby';
import { DateTime } from 'luxon';
import { PapeletaData, PapeletaEventData } from '@arema/types/internal';
import { useTitle } from '@arema/components/Hooks';
import { Money } from '@arema/components';
import API from '../../API';

const ExtrasLocale : { [x in keyof PapeletaData["extras"]]?: string } = {
	'payment_cost': 'Comisión bancaria',
	// 'commission': 'Cargo servicio',
	'delivery_cost': 'Cargo entrega'
}

type ExtrasKey = keyof PapeletaData["extras"];
enum ShownType{
	ALL = 1,
	T2 = 2,
	T3 = 3,
}

interface PapeletaDayProps{
	month?: boolean
}
var PapeletaDay = (props: PapeletaDayProps)=>{
	var { t } = useTranslation();
	var { setTitle } = useTitle();
	var { day } = useParams<{ day: string }>();
	var [data, setData] = useState<PapeletaData>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [showingAll, setShowingAll] = useState<boolean>(false);
	var [shownType, setShownType] = useState<ShownType>(ShownType.ALL);
	var [businessShown, setBusinessShown] = useState<number>(null);
	var [remaningModal, setRemainingModal] = useState<boolean>(null);

	var { date_start, date_end } = useMemo(()=>{
		var date_start : DateTime = null, date_end: DateTime = null;
		if(day){
			if(props.month){
				date_start = DateTime.fromFormat(day, 'MM-yyyy').startOf('month').setLocale('es');
			}else{
				date_start = DateTime.fromFormat(day, 'yyyy-MM-dd').startOf('day').setLocale('es');
			}
			if(date_start.isValid){
				date_end = date_start.endOf(props.month ? 'month' : 'day');
			}else{
				date_start = null;
				date_end = null;
			}
		}
		return { date_start, date_end };
	}, [day]);
	
	useEffect(()=>{
		if(!date_start) return;

		setTitle(`Papeleta ${date_start.toFormat(props.month ? 'MMM/yy' : 'dd/MMM/yy')}`);
		if(!data){
			API.getPapeletaDay(date_start.toFormat('yyyy-MM-dd'), date_end?.toFormat('yyyy-MM-dd') || null).then(res=>{
				if(res.error) return setLoadError(res.message);
				console.log(res.data);
				setData(res.data);
			}).catch(err=>{
				return setLoadError('Hubo un error cargando la papeleta (LCL-1)');
			});
		}
	}, []);

	var {
		shown_events,
		event_count,
		extra_charges,
		payment_methods,
		totals,
		business
	} = useMemo(()=>{
		var event_count = 0;
		var totals : PapeletaData["totals"] = {
			deposit: 0,
			chargeback: 0,
			refund: 0,
			remaining: { deposit: 0, chargeback: 0, refund: 0, orders: [] },
			legacy: null,
		}
		var shown_events : PapeletaEventData[] = [];
		var extra_charges : PapeletaData["extras"] = {
			commission: { deposit: 0, chargeback: 0, legacy: null, refund: 0, },
			payment_cost: { deposit: 0, chargeback: 0, legacy: null, refund: 0, },
			delivery_cost: { deposit: 0, chargeback: 0, legacy: null, refund: 0, },
		}
		var payment_methods : PapeletaData["payment_methods"] = [];
		var business : PapeletaData["business"] = [];

		if(!!data){
			var bx = businessShown ? data.business.findIndex(a=>a.business_id===businessShown) : -1;
			var evs : PapeletaData["events"] = data.events;
			if(businessShown){
				if(bx!==-1){
					evs = data.business[bx].events;
				}
			}

			// ===== EVENTS =====
			for(var i of evs){
				var ev : PapeletaEventData = {
					event_id: i.event_id,
					event_name: i.event_name,
					deposit: 0,
					chargeback: 0,
					refund: 0,
					legacy: null,
				}
				if(shownType===ShownType.T3 || shownType===ShownType.ALL){
					ev = structuredClone(i);
				}
				if(shownType===ShownType.T2 || shownType===ShownType.ALL){
					ev.deposit += i.legacy.deposit;
					ev.chargeback += i.legacy.chargeback;
					ev.refund += i.legacy.refund;
				}

				if(toCents(Math.abs(ev.deposit))>0 || toCents(Math.abs(ev.refund))>0 || toCents(Math.abs(ev.chargeback))>0){
					shown_events.push(ev);
				}
			}

			// ===== EXTRAS =====
			var extras : PapeletaData["extras"] = data.extras;
			if(bx!==-1) extras = data.business[bx].extras;
			var totals_amount : PapeletaData["totals"] = data.totals;
			if(bx!==-1) totals_amount = data.business[bx].totals;
			
			if(shownType===ShownType.T3 || shownType===ShownType.ALL){
				for(var k of ["commission", "payment_cost", "delivery_cost"]){
					(extra_charges as any)[k].deposit += (extras as any)[k].deposit;
					(extra_charges as any)[k].chargeback += (extras as any)[k].chargeback;
					(extra_charges as any)[k].refund += (extras as any)[k].refund;
				}
				
				for(var k of ["deposit", "chargeback", "refund"]){
					(totals as any)[k] += (totals_amount as any)[k];
					(totals.remaining as any)[k] += ((totals_amount.remaining as any)[k] || 0);
				}
				totals.remaining.orders.push(...totals_amount.remaining.orders);
			}
			if(shownType===ShownType.T2 || shownType===ShownType.ALL){
				for(var k of ["commission", "payment_cost", "delivery_cost"]){
					(extra_charges as any)[k].deposit += (extras as any)[k].legacy.deposit;
					(extra_charges as any)[k].chargeback += (extras as any)[k].legacy.chargeback;
					(extra_charges as any)[k].refund += (extras as any)[k].legacy.refund;
				}
				
				for(var k of ["deposit", "chargeback", "refund"]){
					(totals as any)[k] += (totals_amount.legacy as any)[k];
					(totals.remaining as any)[k] += ((totals_amount.legacy.remaining as any)[k] || 0);
				}
				totals.remaining.orders.push(...totals_amount.legacy.remaining.orders);
			}

			// ===== PAYMENT METHODS =====
			var paymet : PapeletaData["payment_methods"] = data.payment_methods;
			if(bx!==-1) paymet = data.business[bx].payment_methods;

			for(var p of paymet){
				var pm : PapeletaData["payment_methods"][0] = {
					method_id: p.method_id,
					method_name: p.method_name,
					chargeback: 0,
					deposit: 0,
					refund: 0,
					legacy: null
				};

				if(shownType===ShownType.T3 || shownType===ShownType.ALL){
					pm = structuredClone(p);
				}
				if(shownType===ShownType.T2 || shownType===ShownType.ALL){
					pm.deposit += p.legacy.deposit;
					pm.chargeback += p.legacy.chargeback;
					pm.refund += p.legacy.refund;
				}
				
				if(toCents(Math.abs(pm.deposit))>0 || toCents(Math.abs(pm.refund))>0 || toCents(Math.abs(pm.chargeback))>0){
					payment_methods.push(pm);
				}
			}

			// ===== BUSINESS =====
			for(var b of data.business){
				if(businessShown && b.business_id!==businessShown) continue;
				var bs : PapeletaData["business"][0] = {
					business_id: b.business_id,
					business_name: b.business_name,
					events: [],
					sources: [],
					payment_methods: [],
					totals: null,
					extras: null,
				};

				if(shownType===ShownType.T3 || shownType===ShownType.ALL){
					bs = structuredClone(b);
				}
				if(shownType===ShownType.T2 || shownType===ShownType.ALL){
					for(var be of b.events){
						var ex = bs.events.findIndex(a=>a.event_id===be.event_id);
						if(ex===-1){
							ex = bs.events.push({
								event_id: be.event_id,
								event_name: be.event_name,
								deposit: 0,
								chargeback: 0,
								refund: 0,
								legacy: null
							})-1
						}
						bs.events[ex].deposit += be.legacy.deposit;
						bs.events[ex].chargeback += be.legacy.chargeback;
						bs.events[ex].refund += be.legacy.refund;
					}
					for(var bsr of b.sources){
						var bx = bs.sources.findIndex(a=>a.source_id===bsr.source_id);
						if(bx===-1){
							bx = bs.sources.push({
								source_id: bsr.source_id,
								source_name: bsr.source_name,
								chargeback: 0,
								deposit: 0,
								refund: 0,
								legacy: null,
							})-1;
						}
						bs.sources[bx].deposit += bsr.legacy.deposit;
						bs.sources[bx].chargeback += bsr.legacy.chargeback;
						bs.sources[bx].refund += bsr.legacy.refund;
					}
				}
				
				business.push(bs);
			}
		}


		shown_events = shown_events.sort(firstBy('event_id', 'asc'));
		event_count = shown_events.length;
		if(!showingAll){
			shown_events = shown_events.slice(0, 10);
		}

		console.log({ totals, event_count, shown_events, extra_charges, payment_methods, business, });
		// console.log(business[0].events.reduce((a,b)=>a+b.deposit, 0)+extra_charges.delivery_cost.deposit+extra_charges.payment_cost.deposit);

		return {
			totals,
			event_count,
			shown_events,
			extra_charges,
			payment_methods,
			business,
		}
	}, [data, showingAll, shownType, businessShown]);

	if(!date_start) return <NotFound />
	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	if(!data) return <Header loading text={t('common.loading')} subtext={`Papeleta ${date_start.toFormat('dd/MMM/yyyy')}`} />
	
	return <div style={{ maxWidth: 800, margin: 'auto' }}>
		<Header text='Papeleta' subtext={date_start.toFormat('dd/MMM/yyyy')} />
		<Stat label={'Total'} value={addCommas(totals.deposit-totals.refund-totals.chargeback)} />
		<Field amount={2} style={{ maxWidth: 800, width: '100%', margin: 'auto', marginBottom: 15 }}>
			<Dropdown label='Negocio' value={businessShown} onValueChange={setBusinessShown} items={[
				{ text: 'Todos', value: null },
				...(data ? data.business.map(a=>({
					text: a.business_name,
					value: a.business_id,
				})) : [])
			]} />
			<Dropdown label='Sistema' value={shownType} onValueChange={setShownType} items={[
				{ text: 'Todos', value: ShownType.ALL },
				{ text: 'Solo T3', value: ShownType.T3 },
				{ text: 'Solo T2', value: ShownType.T2 },
			]} />
		</Field>
		<Table striped>
			<Table.Head data={[['ID', 'Evento', 'Deposito', 'Devolución', 'CCargo']]} collapsingIndexes={[0, 2, 3, 4]} />
			<Table.Body>
				{shown_events.map(a=>(
					<Table.Row key={`PPL-${a.event_id}`} collapsingIndexes={[0, 2, 3, 4]} data={[
						a.event_id,
						a.event_name,
						<Money value={a.deposit} />,
						<Money value={a.refund} />,
						<Money value={a.chargeback} />,
					]} />
				))}
				{!showingAll && shown_events.length!==event_count && (
					<Table.Row>
						<Table.Cell compact colSpan={100}>
							<Button style={{ margin: 5, padding: 15, borderRadius: 8, textAlign: 'center', fontWeight: 'bold', display: 'block' }} text={`Mostrar ${event_count-shown_events.length} eventos mas...`} onClick={bindClick(setShowingAll, true)} />
						</Table.Cell>
					</Table.Row>
				)}
				<Table.Divider />
				{Object.keys(ExtrasLocale).map(a=>(
					<Table.Row
						key={`EXTR-${a}`}
						collapsingIndexes={[0, 2, 3, 4]}
						data={[
							<Icon name='minus' color='gray' />,
							<b>{(ExtrasLocale as any)[a]}</b>,
							<Money value={extra_charges[a as ExtrasKey].deposit} />,
							<Money value={extra_charges[a as ExtrasKey].refund} />,
							<Money value={extra_charges[a as ExtrasKey].chargeback} />,
						]}
					/>
				))}
				{!!totals.remaining && Math.abs(totals.remaining.orders.length)>0 && (
					<Table.Row
						collapsingIndexes={[0, 2, 3, 4]}
						data={[
							<Icon name='triangle-exclamation' color='brown' />,
							<>
								<b>Reserva</b>
								<div className="meta">
									<Button size='tiny' text='Ver reserva' onClick={bindClick(setRemainingModal, true)} />
								</div>
							</>,
							<Money value={totals.remaining.deposit} />,
							<Money value={totals.remaining.refund} />,
							<Money value={totals.remaining.chargeback} />,
						]}
					/>
				)}
				<Table.Divider />
				<Table.Row style={{ fontWeight: 'bold' }}>
					<Table.Cell colSpan={2} style={{ textAlign: 'right', fontWeight: 'bold' }}>
						Total
					</Table.Cell>
					<Table.Cell><Money value={totals.deposit} /></Table.Cell>
					<Table.Cell><Money value={totals.refund} /></Table.Cell>
					<Table.Cell><Money value={totals.chargeback} /></Table.Cell>
				</Table.Row>
			</Table.Body>
		</Table>
		<Table striped style={{ margin: 'auto', marginTop: 15, maxWidth: 700 }}>
			<Table.Head 
				title='Formas de pago'
				data={[['ID', 'Método', 'Deposito', 'Devolución', 'Contracargo', 'Total']]} 
			/>
			<Table.Body>
				{payment_methods.map(a=>(
					<Table.Row collapsingIndexes={[0, 2, 3, 4, 5]} key={`MTH-${a.method_id}`} data={[
						a.method_id,
						a.method_name,
						<Money value={a.deposit} />,
						<Money value={a.refund} />,
						<Money value={a.chargeback} />,
						<Money value={a.deposit-a.refund-a.chargeback} />,
					]} />
				))}
			</Table.Body>
		</Table>
		<Table style={{ margin: 'auto', marginTop: 15, maxWidth: 700 }}>
			<Table.Head title='Origenes' data={[['ID', 'Negocio', 'Deposito', 'Devolución', 'Contracargo', 'Total']]} />
			<Table.Body>
				{business.map((a, ix, arr)=>{
					var bsn_totals : PapeletaCurrency = {
						chargeback: 0,
						deposit: 0,
						refund: 0,
					}
					for(var i of a.sources){
						bsn_totals.deposit += i.deposit;
						bsn_totals.refund += i.refund;
						bsn_totals.chargeback += i.chargeback;
					}
					return <React.Fragment key={`BSN-${a.business_id}`}>
						<Table.Row 
							style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold', borderBottom: '1px solid #EEE' }}
							collapsingIndexes={[0, 2, 3, 4, 5]}
							data={[
								a.business_id,
								a.business_name,
								<Money value={bsn_totals.deposit} />,
								<Money value={bsn_totals.refund} />,
								<Money value={bsn_totals.chargeback} />,
								<Money value={bsn_totals.deposit-bsn_totals.refund-bsn_totals.chargeback} />,
							]}
						/>
						{a.sources.map(s=>(
							<Table.Row
								key={`SRC-${s.source_id}`}
								collapsingIndexes={[0, 2, 3, 4, 5]}
								data={[
									(s.source_id || <Icon name='minus' color='gray' />),
									(s.source_name || <Icon name='minus' color='gray' />),
									<Money value={s.deposit} />,
									<Money value={s.refund} />,
									<Money value={s.chargeback} />,
									<Money value={s.deposit-s.refund-s.chargeback} />,
								]}
							/>
						))}
						{ix<(arr.length-1) && (
							<Table.Divider cellStyle={{ backgroundColor: '#EEE' }} />
						)}
					</React.Fragment>
				})}
			</Table.Body>
		</Table>
		<Modal open={remaningModal} onClose={bindClick(setRemainingModal)} size='tiny'>
			<Modal.Header>Reserva</Modal.Header>
			<Modal.Content>
				<Table fitted top last>
					<Table.Head collapsingIndexes={[0, 2]} data={[['ID', 'Orden', 'Cantidad']]} />
					<Table.Body>
						{totals.remaining.orders.map(a=>(
							<Table.Row key={`RMTL-${a.payment_id}`} collapsingIndexes={[0, 2]}>
								<Table.Cell>{a.payment_id}</Table.Cell>
								<Table.Cell>
									<Link to={`/orders/@${a.order_id}`} target={'_blank'} className={'normal'}>
										{a.order_id}
									</Link>
								</Table.Cell>
								<Table.Cell><Money value={a.amount} /></Table.Cell>
							</Table.Row>
						))}
					</Table.Body>
				</Table>
			</Modal.Content>
			<Modal.Actions>
				<Button text={t('common.close')} onClick={bindClick(setRemainingModal)} />
			</Modal.Actions>
		</Modal>
	</div>
}

export default PapeletaDay;